/*
 * Which element the keyboard scrolls, and where it stands as a page arrives. Chrome sends Space,
 * Page Down and the arrows to the document while nothing has focus, and the document doesn't
 * scroll: the page scroll does. So the page scroll takes focus as each page arrives, but only focus
 * nobody holds. These are its rules, kept apart from its component so they can be checked without a
 * browser.
 */

/**
 * Where focus is as a page arrives: `nowhere` (on the body); on a `clicked` link outside the page,
 * as the sidebar's is once the pointer follows it; `elsewhere` outside the page (a link reached from
 * the keyboard, a dialog, the command palette); or inside the page scroll, where `page` is the path
 * it was taken on.
 */
export type FocusAt = "nowhere" | "clicked" | "elsewhere" | { page: string }

/**
 * Whether the page scroll takes focus as the page at `pathname` arrives: when nothing has it, a
 * link the pointer followed has it, or the page before this one still does, as a link on it that
 * was followed. Focus the new page took for itself (a field it focuses as it opens) stays where it
 * is, as does focus the keyboard put outside the page.
 */
export function takesFocus(focus: FocusAt, pathname: string) {
  if (focus === "nowhere" || focus === "clicked") return true
  if (focus === "elsewhere") return false
  return focus.page !== pathname
}

/** Where focus arriving on the page came from: an overlay, closing or already gone, or elsewhere. */
export type FocusFrom = "overlay" | "elsewhere"

/**
 * Whether focus arriving on a control inside the page goes back to the page scroll: when the page
 * scroll itself held it as an overlay opened (a dialog, a menu, the command palette), and the
 * overlay is handing it back as it closes. Base UI hands focus back to the first control inside an
 * element that Tab doesn't stop at, as it doesn't at the page scroll, which would leave the Member
 * on the page's first crumb with its ring showing.
 */
export function handsBack(heldItself: boolean, from: FocusFrom) {
  return heldItself && from === "overlay"
}

/** How an entry was arrived at, as the router says: Back or Forward, a link, or a replacement. */
export type Arrival = "POP" | "PUSH" | "REPLACE"

/**
 * Where the page scroll stands as an entry arrives, on the compact layout, where a list's ticket
 * opens as its page: Back or Forward returns to where it was left on that entry (`saved`), so a
 * list read halfway down is still halfway down; a new page starts at its top; and a change to the
 * same page's address (a filter, a sheet's choice) keeps the place it has. A page returned to with
 * no place kept for it, as after a reload, starts at its top.
 */
export function arrivingScroll({
  arrival,
  samePage,
  saved,
}: {
  arrival: Arrival
  samePage: boolean
  saved: number | undefined
}): number | "kept" {
  if (arrival === "POP" && saved !== undefined) return saved
  return samePage ? "kept" : 0
}
